import type {DibDocument} from "./types";

export interface TocEntry {
    id: string; // section element ID (UUID)
    title: string; // display text
    seg?: string; // boolish number 1 or undefined
    children: TocEntry[];
}

/**
 * Transforms the flat toc map + tocroot into a renderable tree.
 * Returns an empty array if no TOC data exists.
 */
export const buildTocTree = (doc: DibDocument): TocEntry[] => {
    if (!doc.tocroot?.length || !doc.toc) return [];

    const rootNode = doc.toc[doc.tocroot[0].id];
    if (!rootNode?.children) return [];

    const buildEntry = (id: string): TocEntry | null => {
        const node = doc.toc[id];
        if (!node?.attributes?.title) return null;

        const ret: TocEntry = {
            id: node.attributes.id,
            title: node.attributes.title,
            children:
                node.children
                    ?.map((c) => buildEntry(c.id))
                    .filter((e): e is TocEntry => e !== null) ?? [],
        };

        if ("seg" in node.attributes) {
            ret.seg = node.attributes.seg;
        }

        return ret;
    };

    return rootNode.children
        .map((c) => buildEntry(c.id))
        .filter((e): e is TocEntry => e !== null);
};

export interface FlatTocEntry {
    id: string;
    title: string;
    depth: number; // 0 for a top-level heading, 1+ for a sub-heading
}

/**
 * Flattens a TOC tree into document order.
 * Each entry keeps its nesting depth for tick-size and indent rules.
 */
export const flattenTocEntries = (
    entries: TocEntry[],
    depth = 0
): FlatTocEntry[] =>
    entries.flatMap((entry) => [
        {id: entry.id, title: entry.title, depth},
        ...flattenTocEntries(entry.children, depth + 1),
    ]);
